"use client";

import FillInTheGapCanvas from "@/components/FillInTheGapCanvas";
import { ResponsiveLanguagePicker } from "@/components/language/ResponsiveLanguagePicker";
import languages from "@/data/languages.json";
import { ArrowRight, BookOpen, ChevronDown } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";

type LanguageLevel = "absolute_beginner" | "beginner" | "intermediate" | "advanced";

const LEVELS: { value: LanguageLevel; label: string; description: string }[] = [
  { value: "absolute_beginner", label: "Absolute beginner", description: "A0" },
  { value: "beginner", label: "Beginner", description: "A1–A2" },
  { value: "intermediate", label: "Intermediate", description: "B1–B2" },
  { value: "advanced", label: "Advanced", description: "C1–C2" },
];

export default function GapsPage() {
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [selectedLanguageCode, setSelectedLanguageCode] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<LanguageLevel>("beginner");
  const [hasStarted, setHasStarted] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleLanguageSelect = (name: string, code: string) => {
    setSelectedLanguage(name);
    setSelectedLanguageCode(code);
    setIsLanguageDropdownOpen(false);
    setSearchQuery("");
  };

  const handleStartPractice = () => {
    if (selectedLanguage && selectedLanguageCode) setHasStarted(true);
  };

  if (hasStarted && selectedLanguage && selectedLanguageCode) {
    return (
      <FillInTheGapCanvas
        selectedLanguage={selectedLanguage}
        selectedLanguageCode={selectedLanguageCode}
        selectedLanguageLevel={selectedLevel}
        onClose={() => setHasStarted(false)}
      />
    );
  }

  return (
    <main className="min-h-[100dvh] bg-[#F5F5F0] text-[#171714]">
      <header className="border-b border-black/10">
        <div className="mx-auto flex min-h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link href="/" className="flex min-h-11 items-center gap-2 rounded-lg">
            <Image
              src="/ui/Chickytutor_bird.png"
              alt=""
              width={40}
              height={40}
              className="rounded-full"
            />
            <span className="font-semibold">ChickyTutor</span>
          </Link>
          <Link
            href="/"
            className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-medium text-black/65 transition-colors hover:text-black"
          >
            Speaking practice
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </header>

      <div className="mx-auto grid w-full max-w-6xl gap-10 px-4 py-10 sm:px-6 sm:py-16 lg:grid-cols-[minmax(0,0.9fr)_minmax(28rem,1.1fr)] lg:gap-16 lg:py-24">
        <section className="self-center">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#A84D15]">
            Free visual practice
          </p>
          <h1 className="mt-4 max-w-xl text-4xl font-semibold leading-[1.05] tracking-[-0.035em] sm:text-5xl">
            Find the missing word. Build a stronger sentence.
          </h1>
          <p className="mt-5 max-w-lg text-lg leading-relaxed text-black/60">
            Practise vocabulary and grammar with short exercises matched to your language and level.
            No account needed.
          </p>

          <div className="mt-8 border-l-2 border-[#FD954F] pl-4 text-sm leading-relaxed text-black/60">
            Choose from more than 70 languages. The difficulty adapts as you answer.
          </div>
        </section>

        <section
          className="rounded-lg border border-black/10 bg-white p-5 shadow-sm sm:p-7"
          aria-labelledby="practice-setup-heading"
        >
          <div className="border-b border-black/10 pb-5">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-black/45">
              Practice setup
            </p>
            <h2 id="practice-setup-heading" className="mt-2 text-2xl font-semibold">
              Start with the right challenge
            </h2>
          </div>

          <div className="mt-6">
            <label className="mb-2 block text-sm font-medium" htmlFor="practice-language-trigger">
              Language
            </label>
            <ResponsiveLanguagePicker
              open={isLanguageDropdownOpen}
              onOpenChange={setIsLanguageDropdownOpen}
              trigger={
                <button
                  id="practice-language-trigger"
                  type="button"
                  className="flex min-h-12 w-full items-center justify-between rounded-lg border border-black/15 bg-white px-4 text-left transition-colors hover:border-black/30"
                  aria-expanded={isLanguageDropdownOpen}
                  aria-label={`Language, ${selectedLanguage || "not selected"}`}
                >
                  <span className={selectedLanguage ? "text-black" : "text-black/45"}>
                    {selectedLanguage || "Choose a language"}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 text-black/45 transition-transform motion-reduce:transition-none ${
                      isLanguageDropdownOpen ? "rotate-180" : ""
                    }`}
                    aria-hidden="true"
                  />
                </button>
              }
              languages={languages as Record<string, string>}
              selectedLanguageCode={selectedLanguageCode ?? ""}
              onSelectLanguage={(code) => {
                const entry = Object.entries(languages as Record<string, string>).find(
                  ([, languageCode]) => languageCode === code
                );
                if (entry) handleLanguageSelect(entry[0], entry[1]);
              }}
              searchTerm={searchQuery}
              onSearchTermChange={setSearchQuery}
              title="Choose a practice language"
            />
          </div>

          <fieldset className="mt-6">
            <legend className="mb-2 text-sm font-medium">Your level</legend>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {LEVELS.map((level) => {
                const selected = selectedLevel === level.value;
                return (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() => setSelectedLevel(level.value)}
                    className={`min-h-16 rounded-lg border px-3 py-2 text-left transition-colors ${
                      selected
                        ? "border-[#C96324] bg-[#FFF1E6] text-black"
                        : "border-black/10 bg-[#FAFAF7] text-black/60 hover:border-black/25 hover:text-black"
                    }`}
                    aria-pressed={selected}
                  >
                    <span className="block text-sm font-medium">{level.label}</span>
                    <span className="mt-0.5 block text-xs opacity-60">{level.description}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          <button
            type="button"
            onClick={handleStartPractice}
            disabled={!selectedLanguage}
            className="mt-7 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-lg bg-[#FD954F] px-6 text-base font-semibold text-[#17120E] transition-colors hover:bg-[#F4863D] disabled:cursor-not-allowed disabled:bg-black/[0.07] disabled:text-black/35"
          >
            <BookOpen className="h-5 w-5" aria-hidden="true" />
            Start visual practice
          </button>
        </section>
      </div>
    </main>
  );
}
